import JSZip from 'jszip';
import { triggerBlobDownload } from '@/lib/dbAdmin';
import { EDGE_FUNCTIONS, type EdgeFunctionInfo } from '@/lib/edgeFunctions';

// ─────────────────────────────────────────────────────────────
// Sources des Edge Functions, empaquetées dans le bundle.
// Vite expose chaque `supabase/functions/<slug>/index.ts` en texte brut :
// le SuperAdmin peut donc télécharger l'intégralité du code déployé
// (comme il télécharge déjà la base de données).
// ─────────────────────────────────────────────────────────────

const rawSources = import.meta.glob('../../supabase/functions/*/index.ts', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export interface EdgeFunctionSource {
  slug: string;
  source: string;
}

/** Liste réelle des fonctions présentes dans le projet (dossier supabase/functions). */
export function listEdgeFunctionSources(): EdgeFunctionSource[] {
  return Object.entries(rawSources)
    .map(([path, source]) => {
      const match = path.match(/functions\/([^/]+)\/index\.ts$/);
      return { slug: match ? match[1] : path, source: String(source || '') };
    })
    .filter((fn) => fn.slug)
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

/**
 * Référentiel complet pour l'écran SuperAdmin : les fonctions réellement
 * présentes dans le projet, enrichies des métadonnées quand elles existent.
 */
export function listEdgeFunctions(): Array<EdgeFunctionInfo & { source: string }> {
  const meta = new Map<string, EdgeFunctionInfo>(EDGE_FUNCTIONS.map((fn) => [fn.slug, fn]));
  return listEdgeFunctionSources().map((fn) => {
    const info = meta.get(fn.slug);
    return {
      slug: fn.slug,
      name: info?.name || fn.slug.replace(/-/g, ' '),
      description: info?.description || 'Fonction serveur du back-end Zifek.',
      verifyJwt: info ? info.verifyJwt : true,
      category: info?.category || 'Système',
      source: fn.source,
    };
  });
}

/** Construit une archive ZIP avec tout le code des Edge Functions. */
export async function downloadEdgeFunctionsZip(): Promise<number> {
  const functions = listEdgeFunctionSources();
  const zip = new JSZip();

  functions.forEach((fn) => {
    zip.file(`supabase/functions/${fn.slug}/index.ts`, fn.source);
  });

  zip.file(
    'supabase/functions/README.md',
    [
      '# Edge Functions Zifek — export de code',
      '',
      `Export généré le ${new Date().toISOString()}`,
      '',
      `${functions.length} fonctions exportées.`,
      '',
      '## Redéploiement',
      '```bash',
      'supabase functions deploy <slug>',
      '# fonction publique (sans JWT) :',
      'supabase functions deploy <slug> --no-verify-jwt',
      '```',
      '',
      'Les secrets (clés API, tokens) ne sont PAS inclus dans cet export :',
      'ils se configurent via les secrets du projet (`supabase secrets set`).',
    ].join('\n'),
  );

  const blob = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(`zifek-edge-functions-${new Date().toISOString().slice(0, 10)}.zip`, blob);
  return functions.length;
}